import { App, Button, Popconfirm, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import type { FilePublicShareLinkItem } from '../../../interfaces/FilePublicShareLinkItem';
import { FileService } from '../../../services/fileApiService';
import { useAppStore } from '../../../store/store';
import { ticksToDateSafe } from '../../../utils/dateConversion';
import { copyToClipboard, getExtensionFromFileName, getFilePublicLink } from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import { Icon } from '../../Core/Icon';

const fileService = new FileService();

export interface FilePublicLinksFormProps {
  /** Callback when modal should be closed */
  onClose?: () => void;
}

/**
 * Renders a list of public share links for files and folders,
 * and allows revoking shared links.
 */
export default function FilePublicLinksForm({ onClose }: FilePublicLinksFormProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const formatTimestamp = useFormatTimestamp();

  // State: list of share links, loading indicator, which link is being revoked
  const [list, setList] = useState<FilePublicShareLinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  /**
   * Fetches the list of public share links from the server.
   * Sets loading state and handles errors gracefully.
   */
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fileService.getPublicFileShareLinks();
      setList(data);
    } catch {
      // If fetching fails, reset the list to empty
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handles revoking a public share link.
   * @param fileId File or folder ID of the share link to revoke
   */
  const handleRevoke = useCallback(async (fileId: number) => {
    setRevokingId(fileId);
    try {
      await fileService.deletePublicFileShareLink(fileId);
      setList((prev) => prev.filter((item) => item.fileId !== fileId));
      useAppStore.getState().events.emit.onFilePublicLinkRevoked({ fileId });
    } finally {
      setRevokingId(null);
    }
  }, []);

  /**
   * Copies the public link to the clipboard and shows a success message.
   * @param url The public link to copy
   * @returns void
   */
  const handleCopyLink = useCallback(
    (url: string) => {
      copyToClipboard(url);
      message.success(t('common.linkCopied'));
    },
    [message, t],
  );

  /**
   * Renders a status tag for each share link (expired, exhausted, or active)
   * @param record The share link record
   * @returns JSX element with status tag
   */
  const renderStatusTag = (record: FilePublicShareLinkItem) => {
    if (record.isExpired) {
      return <Tag color="default">{t('modal.filePublicLinksForm.statusExpired')}</Tag>;
    }
    if (record.isExhausted) {
      return <Tag color="orange">{t('modal.filePublicLinksForm.statusExhausted')}</Tag>;
    }
    return <Tag color="green">{t('modal.filePublicLinksForm.statusActive')}</Tag>;
  };

  // Load the links on mount
  useEffect(() => {
    loadList();
  }, [loadList]);

  return (
    <div className="w-full">
      {/* Show loading animation while fetching data */}
      {loading ? (
        <div className="flex w-full items-center justify-center py-8">
          <Icon name="LoaderIcon" className="text-blue-dark h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="max-h-[60vh] w-full overflow-y-auto">
          <ListyWithEmpty
            className="w-full"
            items={list}
            emptyText={t('modal.filePublicLinksForm.empty')}
            rowKey="fileId"
            itemRender={(item: FilePublicShareLinkItem) => (
              <ListyMetaRow
                className="rounded-md px-2"
                avatar={
                  <FileIcon
                    fileExtension={getExtensionFromFileName(item.fileName)}
                    fileId={item.fileId}
                    isFolder={item.isFolder}
                    className="h-8 w-8"
                    iconClassName="h-6 w-6"
                  />
                }
                title={
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      target="_blank"
                      href={getFilePublicLink(item.publicToken)}
                      className="text-blue-dark text-sm font-medium hover:underline!"
                    >
                      {item.fileName}
                    </a>
                    {renderStatusTag(item)}
                  </div>
                }
                description={
                  <div className="text-gray-dark space-y-1 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon name="CalendarClockIcon" className="h-3 w-3" />
                      <span>
                        {t('modal.filePublicLinksForm.expiresAt')}:{' '}
                        {(() => {
                          const expiresDate = ticksToDateSafe(item.expirationAt);
                          return expiresDate != null ? formatTimestamp(expiresDate) : '—';
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="EyeIcon" className="h-3 w-3" />
                      <span>
                        {t('modal.filePublicLinksForm.accessCount')}:{' '}
                        {item.maxAccessCount != null
                          ? `${item.accessCount} / ${item.maxAccessCount}`
                          : String(item.accessCount)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>
                        {t('modal.filePublicLinksForm.hasPassword')}:{' '}
                        {item.hasPassword ? t('common.yes') : t('common.no')}
                      </span>
                    </div>
                  </div>
                }
                actions={
                  <div className="flex items-center gap-1">
                    <Tooltip title={t('common.copyLink')}>
                      <Button
                        type="text"
                        size="small"
                        aria-label={t('a11y.copyLink')}
                        icon={<Icon name="CopyIcon" className="h-4 w-4" />}
                        onClick={() => handleCopyLink(getFilePublicLink(item.publicToken))}
                      />
                    </Tooltip>
                    {!item.isExpired && !item.isExhausted ? (
                      <Popconfirm
                        title={t('common.revokeLink')}
                        description={t('common.revokeLinkConfirmDescription', {
                          fileName: item.fileName,
                        })}
                        onConfirm={() => handleRevoke(item.fileId)}
                        okText={t('common.confirm')}
                        cancelText={t('common.cancel')}
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          size="small"
                          danger
                          loading={revokingId === item.fileId}
                          icon={<Icon name="XIcon" className="h-4 w-4" />}
                        >
                          {t('common.revoke')}
                        </Button>
                      </Popconfirm>
                    ) : null}
                  </div>
                }
              />
            )}
          />
        </div>
      )}

      {onClose ? (
        <div className="border-gray-medium mt-4 flex justify-end gap-2 border-t pt-4">
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      ) : null}
    </div>
  );
}
